import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Icon from '../../components/AppIcon';
import { selectCurrentUser } from '../../features/auth/authSlice';
import { useCurrency } from '../../features/currency/CurrencyContext';
import {
  useStartBoostPaymentMutation,
  useLazyGetBoostPaymentStatusQuery,
} from '../../features/api/apiSlice';

const METHOD_COPY = {
  flutterwave: {
    title: 'Card or mobile money',
    hint: 'Pay on a secure Flutterwave page. Supports cards, MTN, Airtel, and more.',
    icon: 'CreditCard',
  },
  mtn_momo: {
    title: 'MTN Mobile Money',
    hint: 'We send a prompt to your MTN phone. Enter your PIN to finish.',
    icon: 'Smartphone',
  },
  airtel_momo: {
    title: 'Airtel Money',
    hint: 'We send a prompt to your Airtel phone. Enter your PIN to finish.',
    icon: 'Smartphone',
  },
};

const BoostCheckoutModal = ({ property, monetization, onClose, onPaid }) => {
  const user = useSelector(selectCurrentUser);
  const { formatPrice } = useCurrency();
  const [startPay, { isLoading: paying }] = useStartBoostPaymentMutation();
  const [checkStatus] = useLazyGetBoostPaymentStatusQuery();

  const methods = monetization?.payment_methods || [];
  const enabledMethods = methods.filter((m) => m.enabled);
  const [method, setMethod] = useState(enabledMethods[0]?.id || 'flutterwave');
  const [phone, setPhone] = useState(user?.mobile || user?.phone || '');
  const [message, setMessage] = useState(null);
  const [polling, setPolling] = useState(false);

  const fee = Number(monetization?.boost_fee_ugx || 0);
  const days = Number(monetization?.boost_days || 7);
  const isFree = fee <= 0;

  useEffect(() => {
    if (enabledMethods.length && !enabledMethods.some((m) => m.id === method)) {
      setMethod(enabledMethods[0].id);
    }
  }, [enabledMethods, method]);

  const needsPhone = useMemo(
    () => enabledMethods.find((m) => m.id === method)?.needs_phone,
    [enabledMethods, method]
  );

  const pollUntilPaid = async (txRef) => {
    setPolling(true);
    const started = Date.now();
    while (Date.now() - started < 120_000) {
      try {
        const res = await checkStatus(txRef).unwrap();
        if (res?.paid) {
          setPolling(false);
          setMessage({ tone: 'ok', text: res.message || 'Payment received. Your listing is boosted.' });
          onPaid?.();
          return;
        }
        if (res?.status === 'failed') {
          setPolling(false);
          setMessage({ tone: 'err', text: res.message || 'Payment was not approved.' });
          return;
        }
      } catch {
        /* keep polling */
      }
      await new Promise((r) => setTimeout(r, 2500));
    }
    setPolling(false);
    setMessage({
      tone: 'warn',
      text: 'Still waiting for the phone approval. Approve it, then refresh this page.',
    });
  };

  const onPay = async () => {
    setMessage(null);
    if (!isFree && needsPhone && !String(phone).replace(/\D/g, '').length) {
      setMessage({ tone: 'err', text: 'Enter the mobile money number that will receive the prompt.' });
      return;
    }
    try {
      const res = await startPay({
        property_id: property.id,
        method,
        phone,
        return_url: `${window.location.origin}/agent-dashboard/properties`,
      }).unwrap();
      if (res.activated) {
        setMessage({ tone: 'ok', text: res.message || 'Listing boosted.' });
        onPaid?.();
        return;
      }
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      if (res.poll && res.tx_ref) {
        setMessage({ tone: 'ok', text: res.instructions || res.message || 'Approve the prompt on your phone.' });
        pollUntilPaid(res.tx_ref);
        return;
      }
      setMessage({ tone: 'warn', text: res.message || 'Payment started.' });
    } catch (err) {
      setMessage({
        tone: 'err',
        text: err?.data?.message || err?.error || 'Could not start payment.',
      });
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/50"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">Boost listing</p>
            <h3 className="font-semibold text-slate-900 mt-0.5">Move this property to the top of search</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 p-1"
            aria-label="Close checkout"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-3">
            <p className="text-sm font-medium text-slate-900 truncate">{property?.title || `Listing #${property?.id}`}</p>
            <p className="text-xs text-slate-500 mt-1">
              {days} days at the top of search · {isFree ? 'No fee' : formatPrice(fee, 'UGX')}
            </p>
          </div>

          {message && (
            <div
              className={`rounded-xl border px-3 py-2.5 text-sm ${
                message.tone === 'ok'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                  : message.tone === 'warn'
                    ? 'border-amber-200 bg-amber-50 text-amber-900'
                    : 'border-rose-200 bg-rose-50 text-rose-900'
              }`}
            >
              {message.text}
            </div>
          )}

          {isFree ? (
            <button
              type="button"
              onClick={onPay}
              disabled={paying}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
            >
              {paying ? 'Boosting…' : 'Boost this listing'}
            </button>
          ) : (
            <>
              <div className="space-y-2">
                {(enabledMethods.length ? enabledMethods : methods).map((m) => {
                  const copy = METHOD_COPY[m.id] || { title: m.label, hint: '', icon: 'Wallet' };
                  const disabled = !m.enabled;
                  return (
                    <label
                      key={m.id}
                      className={`flex items-start gap-3 rounded-xl border px-3 py-3 cursor-pointer ${
                        method === m.id ? 'border-amber-400 bg-amber-50/60' : 'border-slate-200'
                      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="radio"
                        name="boost-pay-method"
                        className="mt-1"
                        disabled={disabled}
                        checked={method === m.id}
                        onChange={() => setMethod(m.id)}
                      />
                      <Icon name={copy.icon} size={18} className="text-slate-700 mt-0.5" />
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{copy.title}</span>
                        <span className="block text-xs text-slate-500">
                          {disabled ? 'Not configured yet. Ask admin to add API keys.' : copy.hint}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>

              {needsPhone && (
                <label className="block">
                  <span className="text-sm font-medium text-slate-800">Mobile money number</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="0770 000 000"
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>
              )}

              <button
                type="button"
                onClick={onPay}
                disabled={paying || polling || !enabledMethods.length}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-5 py-3 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60"
              >
                <Icon name="Lock" size={16} />
                {paying || polling
                  ? polling
                    ? 'Waiting for phone approval…'
                    : 'Starting payment…'
                  : `Pay ${formatPrice(fee, 'UGX')}`}
              </button>
              {!enabledMethods.length && (
                <p className="text-xs text-rose-600">
                  No payment method is enabled. Turn on Flutterwave, MTN, or Airtel in admin → Payment methods.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoostCheckoutModal;
